import React, { useEffect, useState, useRef, Component } from 'react';
import { ShieldCheck, Lightbulb, Award, Zap, ClipboardList, Brain, Monitor, Shield, Briefcase, Rocket, CheckCircle2, ArrowRight, Menu, X, ChevronDown, Mail, Phone, MapPin, Globe, Users, Clock } from 'lucide-react';
// --- Utility Components ---
// FadeIn Animation Component using IntersectionObserver
function FadeIn({
  children,
  delay = 0,
  className = '',
  direction = 'up'
}: {
  children: ReactNode;
  delay?: number;
  className?: string;
  direction?: 'up' | 'down' | 'left' | 'right' | 'none';
}) {
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setIsVisible(true);
        observer.unobserve(entry.target);
      }
    }, {
      threshold: 0.1,
      rootMargin: '50px'
    });
    if (ref.current) {
      observer.observe(ref.current);
    }
    return () => {
      if (ref.current) observer.unobserve(ref.current);
    };
  }, []);
  const getDirectionClass = () => {
    if (!isVisible) {
      switch (direction) {
        case 'up':
          return 'translate-y-10 opacity-0';
        case 'down':
          return '-translate-y-10 opacity-0';
        case 'left':
          return 'translate-x-10 opacity-0';
        case 'right':
          return '-translate-x-10 opacity-0';
        case 'none':
          return 'opacity-0';
        default:
          return 'translate-y-10 opacity-0';
      }
    }
    return 'translate-y-0 translate-x-0 opacity-100';
  };
  return <div ref={ref} className={`transition-all duration-1000 ease-out ${getDirectionClass()} ${className}`} style={{
    transitionDelay: `${delay}ms`
  }} data-id="element-2">
      {children}
    </div>;
}
// --- Main Page Component ---
export function LandingPage() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isFormSubmitted, setIsFormSubmitted] = useState(false);
  // Force dark mode
  useEffect(() => {
    document.documentElement.classList.add('dark');
  }, []);
  // Handle scroll effect for header
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsFormSubmitted(true);
    // Reset form after 5 seconds
    setTimeout(() => {
      setIsFormSubmitted(false);
    }, 5000);
  };
  const navLinks = [{
    name: 'About',
    href: '#about'
  }, {
    name: 'Vision',
    href: '#vision'
  }, {
    name: 'Mission',
    href: '#mission'
  }, {
    name: 'Services',
    href: '#services'
  }, {
    name: 'Contact',
    href: '#contact'
  }];
  return <div className="min-h-screen bg-slate-50 dark:bg-slate-950 text-slate-900 dark:text-slate-50 font-sans selection:bg-slate-900 selection:text-white dark:selection:bg-blue-500 overflow-x-hidden transition-colors duration-300" data-id="element-3">
      {/* Navigation */}
      <header className={`fixed top-0 w-full z-50 transition-all duration-300 ${isScrolled || isMobileMenuOpen ? 'bg-white/90 dark:bg-slate-900/90 backdrop-blur-md shadow-sm border-b border-slate-100 dark:border-slate-800 py-3' : 'bg-transparent py-5'}`} data-id="element-4">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-5">
          <div className="flex items-center justify-between" data-id="element-6">
            {/* Logo */}
            <a href="#" className="flex items-center gap-2 group" data-id="element-7">
              <img src="https://cdn.magicpatterns.com/uploads/6Q71oQDgPJiqeMoNACG71V/Logo_BlackBG.png" alt="Aliah AI Logo" className="h-12 w-auto object-contain group-hover:scale-105 transition-transform" data-id="element-8" />
            </a>

            {/* Desktop Nav */}
            <nav className="hidden md:flex items-center gap-8" data-id="element-9">
              {navLinks.map(link => <a key={link.name} href={link.href} className="text-sm font-medium text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white transition-colors relative group" data-id="element-10">
                  {link.name}
                  <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-slate-900 dark:bg-white transition-all group-hover:w-full" data-id="element-11"></span>
                </a>)}

              <a href="#contact" className="px-5 py-2.5 bg-slate-900 dark:bg-white text-white dark:text-slate-900 text-sm font-medium rounded-full hover:bg-slate-800 dark:hover:bg-slate-100 transition-all hover:shadow-lg hover:-translate-y-0.5" data-id="element-12">
                Get Started
              </a>
            </nav>

            {/* Mobile Controls */}
            <div className="flex items-center gap-4 md:hidden" data-id="element-13">
              <button className="p-2 text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white transition-colors" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} aria-label="Toggle menu" data-id="element-14">
                {isMobileMenuOpen ? <X className="w-6 h-6" data-id="element-15" /> : <Menu className="w-6 h-6" data-id="element-16" />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Nav Overlay */}
        {isMobileMenuOpen && <div className="md:hidden absolute top-full left-0 w-full bg-white dark:bg-slate-900 border-b border-slate-100 dark:border-slate-800 shadow-xl animate-in slide-in-from-top-5 duration-200" data-id="element-17">
            <div className="flex flex-col p-4 space-y-4" data-id="element-18">
              {navLinks.map(link => <a key={link.name} href={link.href} className="text-base font-medium text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white hover:bg-slate-50 dark:hover:bg-slate-800 px-4 py-3 rounded-lg transition-colors" onClick={() => setIsMobileMenuOpen(false)} data-id="element-19">
                  {link.name}
                </a>)}
              <a href="#contact" className="text-center px-5 py-3 bg-slate-900 dark:bg-white text-white dark:text-slate-900 font-medium rounded-lg hover:bg-slate-800 dark:hover:bg-slate-100 transition-colors" onClick={() => setIsMobileMenuOpen(false)} data-id="element-20">
                Get Started
              </a>
            </div>
          </div>}
      </header>

      <main data-id="element-21">
        {/* Hero Section */}
        <section className="relative pt-32 pb-20 md:pt-48 md:pb-32 px-4 overflow-hidden" data-id="element-22">
          {/* Background Elements */}
          <div className="absolute inset-0 -z-10 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-slate-100 via-slate-50 to-white dark:from-slate-900 dark:via-slate-950 dark:to-black transition-colors duration-500" data-id="element-23"></div>
          <div className="absolute top-20 right-0 w-96 h-96 bg-blue-50/50 dark:bg-blue-900/20 rounded-full blur-3xl -z-10 opacity-60" data-id="element-24"></div>
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-slate-100/50 dark:bg-slate-800/20 rounded-full blur-3xl -z-10 opacity-60" data-id="element-25"></div>

          <div className="max-w-7xl mx-auto text-center relative z-10" data-id="element-26">
            <FadeIn delay={100} direction="up" data-id="element-27">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-slate-100 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 text-slate-600 dark:text-slate-300 text-xs font-semibold tracking-wide uppercase mb-8" data-id="element-28">
                <span className="w-2 h-2 rounded-full bg-blue-500 animate-pulse" data-id="element-29"></span>
                Innovating the Future
              </div>
            </FadeIn>

            <FadeIn delay={200} direction="up" data-id="element-30">
              <h1 className="text-5xl md:text-7xl font-bold tracking-tight text-slate-900 dark:text-white mb-6 leading-tight" data-id="element-31">
                Empowering Businesses <br data-id="element-32" />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-slate-700 to-slate-900 dark:from-slate-200 dark:to-white" data-id="element-33">
                  Through Intelligent Technology
                </span>
              </h1>
            </FadeIn>

            <FadeIn delay={300} direction="up" data-id="element-34">
              <p className="text-xl md:text-2xl text-slate-600 dark:text-slate-400 max-w-2xl mx-auto font-light mb-10 leading-relaxed" data-id="element-35">
                Forward-thinking strategies designed to meet the challenges of a
                dynamic world. We empower organizations to innovate, adapt, and
                grow.
              </p>
            </FadeIn>

            <FadeIn delay={400} direction="up" data-id="element-36">
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4" data-id="element-37">
                <a href="#contact" className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-8 py-4 bg-slate-900 dark:bg-white text-white dark:text-slate-900 rounded-full font-semibold hover:bg-slate-800 dark:hover:bg-slate-100 transition-all hover:shadow-xl hover:-translate-y-1" data-id="element-38">
                  Start Your Journey <ArrowRight className="w-4 h-4" data-id="element-39" />
                </a>
                <a href="#services" className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-8 py-4 bg-white dark:bg-slate-900 text-slate-700 dark:text-slate-300 border border-slate-200 dark:border-slate-700 rounded-full font-semibold hover:bg-slate-50 dark:hover:bg-slate-800 hover:border-slate-300 dark:hover:border-slate-600 transition-all hover:shadow-md" data-id="element-40">
                  Explore Services
                </a>
              </div>
            </FadeIn>
          </div>

          {/* Scroll Indicator */}
          <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce text-slate-400 dark:text-slate-600 hidden md:block" data-id="element-41">
            <ChevronDown className="w-6 h-6" data-id="element-42" />
          </div>
        </section>

        {/* Stats Section */}
        <section className="py-12 bg-white dark:bg-slate-900 border-y border-slate-100 dark:border-slate-800 transition-colors duration-300" data-id="element-43">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-44">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center" data-id="element-45">
              {[{
              label: 'Businesses Adopting AI',
              value: '77%',
              icon: Brain
            }, {
              label: 'Productivity Increase',
              value: '40%',
              icon: Zap
            }, {
              label: 'AI Market Growth',
              value: '$1.8T',
              icon: Rocket
            }, {
              label: 'Jobs Enhanced by AI',
              value: '97M',
              icon: Users
            }].map((stat, index) => <FadeIn key={index} delay={index * 100} direction="up" data-id="element-46">
                  <div className="space-y-2 group cursor-default" data-id="element-47">
                    <div className="mx-auto w-10 h-10 rounded-full bg-slate-50 dark:bg-slate-800 flex items-center justify-center text-slate-600 dark:text-slate-400 mb-3 group-hover:bg-slate-900 dark:group-hover:bg-white group-hover:text-white dark:group-hover:text-slate-900 transition-colors duration-300" data-id="element-48">
                      <stat.icon className="w-5 h-5" data-id="element-49" />
                    </div>
                    <div className="text-3xl md:text-4xl font-bold text-slate-900 dark:text-white" data-id="element-50">
                      {stat.value}
                    </div>
                    <div className="text-sm text-slate-500 dark:text-slate-400 font-medium uppercase tracking-wider" data-id="element-51">
                      {stat.label}
                    </div>
                  </div>
                </FadeIn>)}
            </div>
          </div>
        </section>

        {/* About Us Section */}
        <section id="about" className="py-24 px-4 bg-white dark:bg-slate-950 overflow-hidden transition-colors duration-300" data-id="element-52">
          <div className="max-w-7xl mx-auto" data-id="element-53">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center" data-id="element-54">
              <FadeIn direction="right" data-id="element-55">
                <div className="relative" data-id="element-56">
                  <div className="absolute -top-4 -left-4 w-24 h-24 bg-slate-100 dark:bg-slate-800 rounded-tl-3xl -z-10 transition-colors duration-300" data-id="element-57"></div>
                  <div className="absolute -bottom-4 -right-4 w-24 h-24 bg-slate-100 dark:bg-slate-800 rounded-br-3xl -z-10 transition-colors duration-300" data-id="element-58"></div>
                  <img src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80" alt="Team collaboration" className="rounded-2xl shadow-2xl w-full object-cover h-[500px] grayscale hover:grayscale-0 transition-all duration-700" data-id="element-59" />
                </div>
              </FadeIn>

              <div className="space-y-8" data-id="element-60">
                <FadeIn delay={100} data-id="element-61">
                  <span className="text-sm font-bold tracking-wider text-blue-600 dark:text-blue-400 uppercase bg-blue-50 dark:bg-blue-900/30 px-3 py-1 rounded-full" data-id="element-62">
                    Who We Are
                  </span>
                  <h2 className="mt-4 text-3xl md:text-4xl font-bold text-slate-900 dark:text-white leading-tight" data-id="element-63">
                    Empowering Organizations Through Strategic Innovation
                  </h2>
                </FadeIn>

                <FadeIn delay={200} data-id="element-64">
                  <p className="text-lg text-slate-600 dark:text-slate-400 leading-relaxed" data-id="element-65">
                    Aliah AI delivers forward-thinking, technology-powered
                    solutions designed to meet the needs of today's dynamic
                    businesses. With expertise in Project Management, Artificial
                    Intelligence, IT, Cybersecurity, Consultancy, Training, and
                    Human Capital Development.
                  </p>
                </FadeIn>

                <FadeIn delay={300} data-id="element-66">
                  <p className="text-lg text-slate-600 dark:text-slate-400 leading-relaxed" data-id="element-67">
                    We empower organizations to streamline operations,
                    strengthen security, enhance decision-making, and build
                    resilient teams. By combining strategic insight with
                    cutting-edge technologies, we create measurable value and
                    drive sustainable progress.
                  </p>
                </FadeIn>

                <FadeIn delay={400} data-id="element-68">
                  <div className="pt-4" data-id="element-69">
                    <a href="#vision" className="text-slate-900 dark:text-white font-semibold flex items-center gap-2 hover:gap-3 transition-all" data-id="element-70">
                      Discover Our Vision <ArrowRight className="w-4 h-4" data-id="element-71" />
                    </a>
                  </div>
                </FadeIn>
              </div>
            </div>
          </div>
        </section>

        {/* Vision & Mission Split Section */}
        <section className="py-24 bg-slate-900 dark:bg-black text-white relative overflow-hidden transition-colors duration-300" data-id="element-72">
          {/* Decorative background */}
          <div className="absolute top-0 left-0 w-full h-full overflow-hidden opacity-10 pointer-events-none" data-id="element-73">
            <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-blue-500 rounded-full blur-[100px] -translate-y-1/2 translate-x-1/2" data-id="element-74"></div>
            <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-purple-500 rounded-full blur-[100px] translate-y-1/2 -translate-x-1/2" data-id="element-75"></div>
          </div>

          <div className="max-w-7xl mx-auto px-4 relative z-10" data-id="element-76">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-16" data-id="element-77">
              {/* Vision */}
              <div id="vision" className="space-y-8" data-id="element-78">
                <FadeIn direction="right" data-id="element-79">
                  <div className="inline-flex items-center gap-2 text-blue-300 font-semibold tracking-wider uppercase text-sm" data-id="element-80">
                    <Lightbulb className="w-4 h-4" data-id="element-81" /> Our Vision
                  </div>
                  <h2 className="text-3xl md:text-4xl font-bold" data-id="element-82">
                    Destination Future
                  </h2>
                  <div className="p-8 bg-slate-800/50 dark:bg-slate-900/50 backdrop-blur-sm rounded-2xl border border-slate-700 hover:border-slate-600 transition-colors" data-id="element-83">
                    <p className="text-xl md:text-2xl font-medium text-slate-200 italic leading-relaxed" data-id="element-84">
                      "To be the trusted partner businesses rely on for
                      technology solutions that inspire growth, adaptability,
                      and future success."
                    </p>
                  </div>
                </FadeIn>
              </div>

              {/* Mission */}
              <div id="mission" className="space-y-8" data-id="element-85">
                <FadeIn direction="left" delay={200} data-id="element-86">
                  <div className="inline-flex items-center gap-2 text-blue-300 font-semibold tracking-wider uppercase text-sm" data-id="element-87">
                    <Rocket className="w-4 h-4" data-id="element-88" /> Our Mission
                  </div>
                  <h2 className="text-3xl md:text-4xl font-bold" data-id="element-89">
                    Driving Purpose
                  </h2>
                  <div className="space-y-4" data-id="element-90">
                    {['Deliver trusted project management and IT solutions that mitigate risk.', 'Provide seamless vendor coordination and resource optimization.', 'Build lasting partnerships based on transparency and impact.', 'Enable clients to innovate, adapt, and grow through tailored solutions.'].map((mission, index) => <div key={index} className="flex items-start gap-4" data-id="element-91">
                        <div className="mt-1 w-6 h-6 rounded-full bg-blue-500/20 flex items-center justify-center flex-shrink-0" data-id="element-92">
                          <CheckCircle2 className="w-4 h-4 text-blue-400" data-id="element-93" />
                        </div>
                        <p className="text-slate-300 leading-relaxed" data-id="element-94">
                          {mission}
                        </p>
                      </div>)}
                  </div>
                </FadeIn>
              </div>
            </div>
          </div>
        </section>

        {/* Core Values Section */}
        <section className="py-24 px-4 bg-slate-50 dark:bg-slate-900 transition-colors duration-300" data-id="element-95">
          <div className="max-w-7xl mx-auto" data-id="element-96">
            <div className="text-center mb-16 space-y-4" data-id="element-97">
              <FadeIn direction="up" data-id="element-98">
                <span className="text-sm font-semibold tracking-wider text-slate-500 dark:text-slate-400 uppercase" data-id="element-99">
                  Our Principles
                </span>
                <h2 className="text-3xl md:text-4xl font-bold text-slate-900 dark:text-white" data-id="element-100">
                  Core Values
                </h2>
                <p className="text-slate-600 dark:text-slate-400 max-w-2xl mx-auto" data-id="element-101">
                  The fundamental beliefs that guide our actions and decisions.
                </p>
              </FadeIn>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8" data-id="element-102">
              {[{
              icon: <ShieldCheck className="w-8 h-8" data-id="element-103" />,
              title: 'Integrity',
              desc: 'Transparent and ethical operations in every interaction.',
              color: 'bg-blue-50 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400'
            }, {
              icon: <Lightbulb className="w-8 h-8" data-id="element-104" />,
              title: 'Innovation',
              desc: 'Creative, forward-thinking solutions for complex problems.',
              color: 'bg-amber-50 text-amber-600 dark:bg-amber-900/30 dark:text-amber-400'
            }, {
              icon: <Award className="w-8 h-8" data-id="element-105" />,
              title: 'Excellence',
              desc: 'Consistently achieving superior outcomes and high standards.',
              color: 'bg-purple-50 text-purple-600 dark:bg-purple-900/30 dark:text-purple-400'
            }, {
              icon: <Zap className="w-8 h-8" data-id="element-106" />,
              title: 'Agility',
              desc: 'Flexible, adaptive delivery models for changing needs.',
              color: 'bg-emerald-50 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-400'
            }].map((value, index) => <FadeIn key={index} delay={index * 100} direction="up" data-id="element-107">
                  <div className="h-full flex flex-col items-center text-center p-8 rounded-2xl bg-white dark:bg-slate-800 border border-slate-100 dark:border-slate-700 shadow-sm hover:shadow-xl hover:-translate-y-2 transition-all duration-300 group" data-id="element-108">
                    <div className={`mb-6 p-4 rounded-full ${value.color} group-hover:scale-110 transition-transform duration-300`} data-id="element-109">
                      {value.icon}
                    </div>
                    <h3 className="text-xl font-bold mb-3 text-slate-900 dark:text-white" data-id="element-110">
                      {value.title}
                    </h3>
                    <p className="text-slate-600 dark:text-slate-400 text-sm leading-relaxed" data-id="element-111">
                      {value.desc}
                    </p>
                  </div>
                </FadeIn>)}
            </div>
          </div>
        </section>

        {/* Services Section */}
        <section id="services" className="py-24 px-4 bg-white dark:bg-slate-950 relative transition-colors duration-300" data-id="element-112">
          <div className="max-w-7xl mx-auto" data-id="element-113">
            <div className="text-center mb-20 space-y-4" data-id="element-114">
              <FadeIn direction="up" data-id="element-115">
                <span className="text-sm font-semibold tracking-wider text-blue-600 dark:text-blue-400 uppercase bg-blue-50 dark:bg-blue-900/30 px-3 py-1 rounded-full" data-id="element-116">
                  What We Do
                </span>
                <h2 className="mt-4 text-3xl md:text-4xl font-bold text-slate-900 dark:text-white" data-id="element-117">
                  Comprehensive Services
                </h2>
                <p className="text-slate-600 dark:text-slate-400 max-w-2xl mx-auto" data-id="element-118">
                  Tailored solutions to drive your business forward in the
                  digital age.
                </p>
              </FadeIn>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-119">
              {[{
              icon: <Brain className="w-6 h-6" data-id="element-120" />,
              title: 'AI Consultancy',
              desc: 'Strategic implementation of artificial intelligence to optimize business processes and unlock new value streams.'
            }, {
              icon: <Shield className="w-6 h-6" data-id="element-121" />,
              title: 'Cybersecurity Consultancy',
              desc: 'Comprehensive security assessments, strategy development, and protection frameworks for your digital assets.'
            }, {
              icon: <Monitor className="w-6 h-6" data-id="element-122" />,
              title: 'IT Consultancy',
              desc: 'Expert guidance on infrastructure, software selection, and digital transformation initiatives.'
            }, {
              icon: <ClipboardList className="w-6 h-6" data-id="element-123" />,
              title: 'Project Management',
              desc: 'Agile and waterfall methodologies to ensure your projects are delivered on time, within scope, and on budget.'
            }, {
              icon: <Briefcase className="w-6 h-6" data-id="element-124" />,
              title: 'AI Arbitration',
              desc: 'Specialized dispute resolution services for conflicts arising from AI implementation and agreements.'
            }, {
              icon: <ShieldCheck className="w-6 h-6" data-id="element-125" />,
              title: 'Security Arbitration',
              desc: 'Expert mediation and arbitration for cybersecurity incidents, liability, and compliance disputes.'
            }].map((service, index) => <FadeIn key={index} delay={index * 100} direction="up" data-id="element-126">
                  <div className="group h-full p-8 bg-white dark:bg-slate-900 rounded-2xl shadow-sm hover:shadow-xl hover:border-blue-100 dark:hover:border-blue-900 border border-slate-100 dark:border-slate-800 transition-all duration-300 flex flex-col relative overflow-hidden" data-id="element-127">
                    <div className="absolute top-0 left-0 w-1 h-full bg-slate-900 dark:bg-white transform -translate-y-full group-hover:translate-y-0 transition-transform duration-300" data-id="element-128"></div>

                    <div className="mb-6 w-12 h-12 bg-slate-50 dark:bg-slate-800 rounded-lg flex items-center justify-center text-slate-700 dark:text-slate-300 group-hover:bg-slate-900 dark:group-hover:bg-white group-hover:text-white dark:group-hover:text-slate-900 transition-colors duration-300" data-id="element-129">
                      {service.icon}
                    </div>

                    <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-3 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors" data-id="element-130">
                      {service.title}
                    </h3>

                    <p className="text-slate-600 dark:text-slate-400 text-sm leading-relaxed" data-id="element-131">
                      {service.desc}
                    </p>
                  </div>
                </FadeIn>)}
            </div>
          </div>
        </section>

        {/* Contact Section */}
        <section id="contact" className="py-24 px-4 bg-slate-50 dark:bg-slate-900 transition-colors duration-300" data-id="element-132">
          <div className="max-w-3xl mx-auto" data-id="element-133">
            <div className="text-center mb-12 space-y-4" data-id="element-134">
              <FadeIn direction="up" data-id="element-135">
                <span className="text-sm font-semibold tracking-wider text-blue-600 dark:text-blue-400 uppercase bg-blue-50 dark:bg-blue-900/30 px-3 py-1 rounded-full" data-id="element-136">
                  Contact Us
                </span>
                <h2 className="mt-4 text-3xl md:text-4xl font-bold text-slate-900 dark:text-white" data-id="element-137">
                  Get in Touch
                </h2>
                <p className="text-slate-600 dark:text-slate-400 max-w-2xl mx-auto" data-id="element-138">
                  Ready to transform your business? Contact us today for a
                  consultation.
                </p>
              </FadeIn>
            </div>

            <FadeIn delay={200} data-id="element-139">
              <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-xl p-8 md:p-12 border border-slate-100 dark:border-slate-700 transition-colors duration-300" data-id="element-140">
                {isFormSubmitted ? <div className="flex flex-col items-center justify-center py-12 space-y-4" data-id="element-141">
                    <div className="w-16 h-16 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center" data-id="element-142">
                      <CheckCircle2 className="w-8 h-8 text-green-600 dark:text-green-400" data-id="element-143" />
                    </div>
                    <h3 className="text-2xl font-bold text-slate-900 dark:text-white" data-id="element-144">
                      Thank You!
                    </h3>
                    <p className="text-slate-600 dark:text-slate-400 text-center" data-id="element-145">
                      Thank you for contacting us. We'll get back to you soon.
                    </p>
                  </div> : <form className="space-y-6" onSubmit={handleFormSubmit} data-id="element-146">
                    <div className="space-y-2" data-id="element-147">
                      <label htmlFor="name" className="text-sm font-medium text-slate-700 dark:text-slate-300" data-id="element-148">
                        Name
                      </label>
                      <input type="text" id="name" required className="w-full px-4 py-3 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-white focus:border-slate-900 dark:focus:border-white focus:ring-1 focus:ring-slate-900 dark:focus:ring-white outline-none transition-all" placeholder="John Doe" data-id="element-149" />
                    </div>

                    <div className="space-y-2" data-id="element-150">
                      <label htmlFor="email" className="text-sm font-medium text-slate-700 dark:text-slate-300" data-id="element-151">
                        Email
                      </label>
                      <input type="email" id="email" required className="w-full px-4 py-3 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-white focus:border-slate-900 dark:focus:border-white focus:ring-1 focus:ring-slate-900 dark:focus:ring-white outline-none transition-all" placeholder="john@example.com" data-id="element-152" />
                    </div>

                    <div className="space-y-2" data-id="element-153">
                      <label htmlFor="message" className="text-sm font-medium text-slate-700 dark:text-slate-300" data-id="element-154">
                        Message
                      </label>
                      <textarea id="message" rows={6} required className="w-full px-4 py-3 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-white focus:border-slate-900 dark:focus:border-white focus:ring-1 focus:ring-slate-900 dark:focus:ring-white outline-none transition-all resize-none" placeholder="How can we help you?" data-id="element-155"></textarea>
                    </div>

                    <button type="submit" className="w-full py-4 bg-slate-900 dark:bg-white text-white dark:text-slate-900 font-bold rounded-lg hover:bg-slate-800 dark:hover:bg-slate-100 transition-all hover:shadow-lg transform hover:-translate-y-1" data-id="element-156">
                      Send Message
                    </button>
                  </form>}
              </div>
            </FadeIn>
          </div>
        </section>

        {/* Footer */}
        <footer className="py-12 px-4 bg-white dark:bg-slate-950 border-t border-slate-100 dark:border-slate-800 transition-colors duration-300" data-id="element-157">
          <div className="max-w-7xl mx-auto" data-id="element-158">
            <div className="flex flex-col md:flex-row justify-between items-center gap-6" data-id="element-159">
              <div className="flex items-center gap-2" data-id="element-160">
                <img src="https://cdn.magicpatterns.com/uploads/6Q71oQDgPJiqeMoNACG71V/Logo_BlackBG.png" alt="Aliah AI Logo" className="h-12 w-auto object-contain" data-id="element-161" />
              </div>

              <div className="flex gap-8 text-sm font-medium text-slate-500 dark:text-slate-400" data-id="element-162">
                <a href="#" className="hover:text-slate-900 dark:hover:text-white transition-colors" data-id="element-163">
                  Privacy Policy
                </a>
                <a href="#" className="hover:text-slate-900 dark:hover:text-white transition-colors" data-id="element-164">
                  Terms of Service
                </a>
                <a href="#" className="hover:text-slate-900 dark:hover:text-white transition-colors" data-id="element-165">
                  Cookie Policy
                </a>
              </div>

              <p className="text-slate-500 dark:text-slate-400 text-sm" data-id="element-166">
                © {new Date().getFullYear()} Aliah AI. All rights reserved.
              </p>
            </div>
          </div>
        </footer>
      </main>
    </div>;
}